import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

export default function HeroSection() {
  const { t } = useTranslation();

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const features = [
    {
      icon: 'ri-tools-line',
      title: t('hero.feature1Title'),
      desc: t('hero.feature1Desc'),
      color: 'from-sky-500 to-cyan-400',
    },
    {
      icon: 'ri-time-line',
      title: t('hero.feature2Title'),
      desc: t('hero.feature2Desc'),
      color: 'from-cyan-500 to-teal-400',
    },
    {
      icon: 'ri-shield-check-line',
      title: t('hero.feature3Title'),
      desc: t('hero.feature3Desc'),
      color: 'from-sky-600 to-sky-400',
    },
    {
      icon: 'ri-map-pin-user-line',
      title: t('hero.feature4Title'),
      desc: t('hero.feature4Desc'),
      color: 'from-teal-500 to-cyan-400',
    },
  ];

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20"
    >
      {/* Background image with overlay */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/edited_dd748f9d1df4ef4cdf3c851dc193586a_6e8cebb6.jpg"
          alt="background"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/75 to-slate-900/50"></div>
        {/* Subtle cyan glow */}
        <div className="absolute top-1/3 left-1/4 w-80 h-80 bg-cyan-500/20 rounded-full blur-3xl animate-aurora"></div>
        <div className="absolute bottom-1/4 right-1/3 w-64 h-64 bg-sky-500/15 rounded-full blur-3xl animate-aurora" style={{ animationDelay: '3s' }}></div>
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex justify-center mb-6"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-500/20 border border-cyan-400/30 text-cyan-300 text-sm font-medium backdrop-blur-sm">
            <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse-slow inline-block"></span>
            м.Київ, вул.Велика Васильківська 46 · метро Площа Українських Героїв
          </span>
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white text-center mb-6 leading-tight"
        >
          {t('hero.title')}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-lg sm:text-xl text-slate-300 text-center mb-12 max-w-2xl mx-auto"
        >
          {t('hero.subtitle')}
        </motion.p>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12 max-w-5xl mx-auto">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 + index * 0.08 }}
            >
              <div className="group bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/15 hover:bg-white/18 hover:border-cyan-400/40 hover:shadow-lg hover:shadow-cyan-500/10 transition-all duration-300 cursor-default h-full">
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br ${feature.color} mb-3 shadow-lg`}>
                  <i className={`${feature.icon} text-white text-lg`}></i>
                </div>
                <h3 className="text-white font-semibold text-sm mb-1 leading-snug">{feature.title}</h3>
                <p className="text-slate-400 text-xs leading-relaxed">{feature.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <button
            onClick={() => scrollToSection('contacts')}
            className="group w-full sm:w-auto px-8 py-4 rounded-xl bg-gradient-to-r from-sky-500 to-cyan-500 text-white font-semibold text-base shadow-xl shadow-sky-500/30 hover:shadow-sky-500/50 hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            <span className="flex items-center justify-center gap-2">
              <i className="ri-calendar-check-line text-lg"></i>
              <span>{t('cta.bookRepair')}</span>
            </span>
          </button>
          <a
            href="tel:+380957403000"
            className="group w-full sm:w-auto px-8 py-4 rounded-xl bg-white/10 backdrop-blur-sm border border-white/25 text-white font-semibold text-base hover:bg-white/20 hover:border-white/40 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            <span className="flex items-center justify-center gap-2">
              <i className="ri-phone-line text-lg text-cyan-400"></i>
              <span>095 740 30 00</span>
            </span>
          </a>
        </motion.div>
      </div>

      {/* Bottom wave */}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-surface to-transparent"></div>
    </section>
  );
}
